import { useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Compulsory } from 'ts-toolbelt/out/Object/Compulsory';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaDigitalForms } from '../../service';
import { IO } from '../../types';
import { defaultOptions } from '../config';
import { requestKeys } from '../keys';

/**
 * Hook to fetch the PDF of a submission form.
 */
export const useGetSubmissionPDF = <E = unknown, T = IO.GetFormPDFAuthed['output']>(
  req: Compulsory<IO.GetFormPDFAuthed['input'], 'submissionId' | 'companyId'>,
  options?: LimitedSchemaQueryOptions<IO.GetFormPDFAuthed, E, T>
) => {
  const queryKey = requestKeys.submissionPDF(req);
  const query = useQuery({
    queryKey,
    queryFn: () => SchemaDigitalForms.GetFormPDFAuthed(req),
    staleTime: Infinity,
    ...defaultOptions,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

/**
 * Hook to fetch the PDF of a submission form asynchronously.
 */
export const useGetSubmissionPDFAsync = <E = unknown, T = IO.GetFormPDFAuthed['output']>() => {
  const queryClient = useQueryClient();
  const fetchSubmissionPDFAsync = useCallback(
    async (
      req: Compulsory<IO.GetFormPDFAuthed['input'], 'submissionId' | 'companyId'>,
      options?: LimitedSchemaFetchQueryOptions<IO.GetFormPDFAuthed, E, T>
    ) => {
      if (!req.companyId || !req.submissionId) {
        console.warn('Invalid request parameters for fetching submission PDF', req);
        return undefined;
      }

      try {
        const queryKey = requestKeys.submissionPDF(req);
        const response = await queryClient.fetchQuery(queryKey, () => SchemaDigitalForms.GetFormPDFAuthed(req), {
          staleTime: Infinity,
          ...options,
        });

        return {
          data: response,
          queryKey,
        };
      } catch (error) {
        console.error('Error fetching submission PDF', error);
        return undefined;
      }
    },
    [queryClient]
  );

  return {
    fetchSubmissionPDFAsync,
  };
};

/**
 * Hook to fetch the PDF of a specific form in a submission asynchronously.
 */
export const useGetSubmissionFormPDFAsync = <E = unknown, T = IO.GetFormPDFAuthed['output']>() => {
  const queryClient = useQueryClient();
  const fetchSubmissionFormPDFAsync = useCallback(
    async (
      req: Omit<Compulsory<IO.GetFormPDFAuthed['input'], 'submissionId' | 'companyId' | 'formId'>, 'packetId'>,
      options?: LimitedSchemaFetchQueryOptions<IO.GetFormPDFAuthed, E, T>
    ) => {
      if (!req.companyId || !req.submissionId || !req.formId) {
        console.warn('Invalid request parameters for fetching submission form PDF', req);
        return undefined;
      }

      try {
        const queryKey = requestKeys.submissionFormPDF(req);
        const response = await queryClient.fetchQuery(queryKey, () => SchemaDigitalForms.GetFormPDFAuthed(req), {
          staleTime: Infinity,
          ...options,
        });

        return {
          data: response,
          queryKey,
        };
      } catch (error) {
        console.error('Error fetching submission form PDF', error);
        return undefined;
      }
    },
    [queryClient]
  );

  return {
    fetchSubmissionFormPDFAsync,
  };
};
